import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listMopOutputs } from "../../api/client";
import type { MopDeliveryState, MopListResponse } from "../../api/types";
import { formatMopDate, MOP_STATES, MopStatus, operationLabel } from "./mopView";
import "./mop.css";

const PAGE_SIZE = 20;

export function MopList() {
  const [params, setParams] = useSearchParams();
  const rawStatus = params.get("status");
  const status = Object.keys(MOP_STATES).find((key) => key === rawStatus) as MopDeliveryState | undefined;
  const q = params.get("q") ?? "";
  const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);
  const [search, setSearch] = useState(q);
  const [data, setData] = useState<MopListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => {
    let live = true;
    let controller: AbortController | undefined;
    async function load() {
      controller?.abort();
      const current = new AbortController();
      controller = current;
      setLoading(true);
      try {
        const result = await listMopOutputs({ status, q, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }, current.signal);
        if (live && !current.signal.aborted) { setData(result); setError(null); }
      } catch (e) {
        if (live && !current.signal.aborted) setError(e instanceof Error ? e.message : "Impossibile caricare le uscite MOP");
      } finally {
        if (live && !current.signal.aborted) setLoading(false);
      }
    }
    void load();
    const timer = setInterval(() => { void load(); }, 30_000);
    return () => { live = false; controller?.abort(); clearInterval(timer); };
  }, [status, q, page, refresh]);

  function filter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
  }

  return <div className="mop-page">
    <div className="mop-header">
      <div><h1 className="page-title">MOP</h1><p className="text-muted">Richieste programmate coperte da MOP, una uscita per edificio e impianto.</p></div>
      <button type="button" className="btn btn-outline" disabled={loading} onClick={() => setRefresh((v) => v + 1)}>Aggiorna</button>
    </div>
    <section className="card" aria-label="Filtri MOP">
      <form className="mop-toolbar" onSubmit={(event) => { event.preventDefault(); filter("q", search.trim()); }}>
        <div className="mop-search"><label className="form-label" htmlFor="mop-search">Cerca</label>
          <input className="form-input" id="mop-search" value={search} maxLength={200} onChange={(event) => setSearch(event.target.value)} placeholder="Edificio, impianto, MOP o oggetto" /></div>
        <button className="btn btn-primary" type="submit">Cerca</button>
      </form>
      <div className="mop-filters" role="group" aria-label="Stato invio">
        <button type="button" className={`btn ${!status ? "btn-primary" : "btn-outline"}`} aria-pressed={!status} onClick={() => filter("status", "")}>
          Tutte{data ? ` (${Object.values(data.counts).reduce((a, b) => a + b, 0)})` : ""}
        </button>
        {Object.entries(MOP_STATES).map(([key, meta]) => <button type="button" key={key} className={`btn ${status === key ? "btn-primary" : "btn-outline"}`} aria-pressed={status === key} onClick={() => filter("status", key)}>
          {meta.label}{data ? ` (${data.counts[key as MopDeliveryState]})` : ""}
        </button>)}
      </div>
    </section>
    {error && <div className="card mop-error" role="alert">{error}</div>}
    {loading && !data ? <div className="loading-center"><div className="spinner" /><span>Caricamento MOP…</span></div> : data && !error && <section className="card" aria-label="Uscite MOP" aria-busy={loading}>
      {data.items.length === 0 ? <div className="mop-empty">
        <h2>{q || status ? "Nessuna uscita per questi filtri" : "Nessuna uscita MOP ricevuta"}</h2>
        <p className="text-muted">Le richieste con una copertura MOP valida compariranno qui dopo l’elaborazione dell’email.</p>
      </div> : <>
        <div className="table-scroll"><table className="mop-table"><thead><tr><th>Ricevuta</th><th>Edificio / impianto</th><th>Attività</th><th>Stato invio</th><th>Richiesta</th></tr></thead>
          <tbody>{data.items.map((item) => <tr key={item.request_id}>
            <td>{formatMopDate(item.received_at ?? item.created_at)}</td>
            <td><Link className="fw-semibold" to={`/mop/${encodeURIComponent(item.request_id)}${params.size ? `?${params}` : ""}`}>{item.descrizione_edificio || item.codice_edificio}</Link>
              <div className="text-sm text-muted">{item.codice_edificio} · {item.codice_impianto}</div><div className="text-sm">{item.descrizione_impianto}</div></td>
            <td>{operationLabel(item.operazione)}<div className="text-sm text-muted">{item.mop_codes.join(", ")}</div></td>
            <td><MopStatus state={item.delivery_state} /></td>
            <td><Link to={`/mop/${encodeURIComponent(item.request_id)}${params.size ? `?${params}` : ""}`}>{item.subject || "Apri richiesta"}</Link><div className="text-sm text-muted">{item.sender}</div></td>
          </tr>)}</tbody></table></div>
        <div className="mop-pagination"><span className="text-sm text-muted">{data.total} uscite · Pagina {page}</span>
          <div className="mop-actions"><button className="btn btn-outline" disabled={page === 1 || loading} onClick={() => filter("page", String(page - 1))}>Precedente</button>
            <button className="btn btn-outline" disabled={page * PAGE_SIZE >= data.total || loading} onClick={() => filter("page", String(page + 1))}>Successiva</button></div>
        </div>
      </>}
    </section>}
  </div>;
}
